import { useEffect, useMemo, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';

import { useClaim } from '../../ClaimContainer';
import type { Invoice } from '../../types/invoice-types';
import useDataFetcher from '../../useDataFetcher';

interface UseInvoiceParams {
  invoiceId?: number;
  documentId?: number;
  documentSourceId?: string;
}

interface UseInvoiceReturn {
  invoice: Invoice | null;
  isLoading: boolean;
  isError: boolean;
  reloadData: () => Promise<Invoice | null | undefined>;
}

type DataFetcherType = Invoice;

export const useInvoice = ({ invoiceId, documentId, documentSourceId }: UseInvoiceParams): UseInvoiceReturn => {
  const { claim, reloadingUuid } = useClaim({ claimRequired: true });
  const [lastReloadingUuid, setLastReloadingUuid] = useState(reloadingUuid);

  // If no identifier is provided, don't make API calls
  const shouldFetch = !!(invoiceId || documentId || documentSourceId);

  // When looking up by documentId/documentSourceId, the invoice may not exist yet
  const nullIfNotFound = !invoiceId;

  const [url, config] = useMemo(() => {
    let url: string = '';
    const config: AxiosRequestConfig = {};

    if (invoiceId) {
      url = `/api/v1/claims/${claim.id}/invoices/${invoiceId}`;
    } else if (documentSourceId) {
      url = `/api/v1/claims/${claim.id}/invoices/document_source_id/${documentSourceId}`;
    } else if (documentId) {
      url = `/api/v1/claims/${claim.id}/invoices/document/${documentId}`;
    }

    if (nullIfNotFound) {
      config.validateStatus = (status: number) => {
        // Important: when making changes to this function, make sure to update the invoice
        // memo, because it relies on the error key being present only in case of 404
        return status === 200 || status === 404;
      };
    }

    return [url, config];
  }, [invoiceId, documentId, documentSourceId, nullIfNotFound, claim.id]);

  const { isLoading, isError, data, httpStatus, reloadData } = useDataFetcher<DataFetcherType>(url, config, {
    shouldFetch,
  });

  useEffect(() => {
    if (!isLoading && shouldFetch && reloadingUuid !== lastReloadingUuid) {
      reloadData();
      setLastReloadingUuid(reloadingUuid);
    }
  }, [shouldFetch, reloadData, reloadingUuid, isLoading, lastReloadingUuid]);

  const invoice = useMemo(() => {
    if (!shouldFetch) {
      return null;
    }
    if (nullIfNotFound && httpStatus === 404) {
      return null;
    }
    return data || null;
  }, [shouldFetch, nullIfNotFound, httpStatus, data]);

  return {
    invoice,
    isLoading: shouldFetch ? isLoading : false,
    isError: shouldFetch ? isError : false,
    reloadData: async () => {
      if (!shouldFetch) {
        return null;
      }
      const data = await reloadData();
      // reloadData doesn't return httpStatus, so we check for valid invoice by id
      // (404 responses return {message: "..."} without id)
      if (nullIfNotFound && !data?.id) {
        return null;
      }
      return data || null;
    },
  };
};

export default useInvoice;
